// A VISITED KINGDOM: the host's real fields, buildings and people, built read-only from the picture the server sends a guest
// (server/kingdomView.js → GET /api/stays/:id/kingdom). It is a "land" in the landScene sense — { root, spawn, walk, tick, dispose } — so the
// guest arrives, walks around, works for the coffer and goes home exactly as in any other land; only the ground is the host's.
//
//   buildKingdomView(snapshot, progress) → land
//
// Built from the same pieces as the player's own field (synty.js recipes, ground cover, scene/surroundings.js), but nothing here touches the
// player's game state: the host's plots/buildings come from the snapshot, collisions are sampled into this view's own set, and `dispose()`
// frees what it made (the shared Synty geometry and materials are left alone). A kingdom is rebuilt on every visit — it is never cached.

import * as THREE from 'three'
import gsap from 'gsap'
import { PlotTypes, SeasonConfig, GrassConfig, BuildingRecipe, HomeRecipe, FarmRecipe, GroundRecipe, WeedsRecipe } from '../config/balance.js'
import { loadSynty, hasRecipe, buildRecipeGroup, buildGroundCover } from './synty.js'
import { loadSyntyChars, syntyCharsReady, createSyntyChar, scaleOf } from './syntyChars.js'
import { fieldCoords, axialToWorld, key, createHexTileGeometry, HEX_SIZE, GROUND_Y, FIELD_RADIUS, isInFieldWorld } from './hexGrid.js'
import { sampleBlockedCells, isBlockedIn } from './collision.js'
import { buildSyntySurroundings } from './surroundings.js'
import { createCatFigure } from './cat.js'
import { KingdomView } from '../config/destinations.js'

const facing = (rotation) => -rotation * (Math.PI / 3) // one facing = 60°, clockwise seen from above (same as scene/buildings.js)

// a small name sign that floats over the host (canvas sprite)
function nameSprite(title, sub) {
  const cv = document.createElement('canvas'); cv.width = 512; cv.height = 128
  const c = cv.getContext('2d')
  c.fillStyle = 'rgba(20, 20, 18, 0.78)'; c.beginPath(); c.roundRect(4, 4, 504, 120, 18); c.fill()
  c.strokeStyle = 'rgba(233, 226, 207, 0.4)'; c.lineWidth = 3; c.stroke()
  c.fillStyle = '#f1e8d0'; c.textAlign = 'center'; c.textBaseline = 'middle'
  c.font = '700 44px Inter, system-ui, sans-serif'; c.fillText(title.slice(0, 22), 256, 48)
  c.font = '500 24px Inter, system-ui, sans-serif'; c.fillStyle = '#cabf99'; c.fillText(sub.slice(0, 34), 256, 92)
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }))
  sp.scale.set(2.4, 0.6, 1)
  sp.userData.disposable = [tex, sp.material]
  return sp
}

export async function buildKingdomView(snap, progress = () => {}) {
  await loadSynty()
  try { await loadSyntyChars() } catch { /* characters are optional: the field is shown without them */ }
  progress(0.15)

  const root = new THREE.Group()
  root.userData.isBiome = true
  root.userData.landId = 'guest_estate'
  root.visible = false
  const own = [] // geometries / materials / textures made here (to dispose)
  const blocked = new Set()
  let disposed = false

  // ── the ground: raised hex blocks, as in the player's own field ──
  const BLOCK_H = GROUND_Y - 0.02
  const blockGeo = createHexTileGeometry(HEX_SIZE, BLOCK_H), blockMat = new THREE.MeshLambertMaterial({ color: GrassConfig.color })
  own.push(blockGeo, blockMat)
  const coords = fieldCoords()
  const blocks = new THREE.InstancedMesh(blockGeo, blockMat, coords.length)
  const mat4 = new THREE.Matrix4()
  coords.forEach(({ q, r }, i) => { const { x, z } = axialToWorld(q, r); blocks.setMatrixAt(i, mat4.makeTranslation(x, BLOCK_H / 2, z)) })
  blocks.raycast = () => {}
  root.add(blocks)

  // ── what stands on each tile ──
  const plots = snap.layout?.plots ?? {}
  const buildingAt = new Map((snap.layout?.buildings ?? []).map((b) => [b.tileIndex, b]))
  const season = SeasonConfig[snap.season] ? snap.season : null
  const open = [], forest = []
  let homeKey = null
  const placed = [] // { wrap, k }
  const place = (recipeId, k, { rotation = 0, slabColor } = {}) => {
    if (!hasRecipe(recipeId)) return null
    const wrap = buildRecipeGroup(recipeId, k, slabColor != null ? { slabColor } : {})
    if (!wrap) return null
    const [q, r] = k.split(',').map(Number)
    const { x, z } = axialToWorld(q, r)
    wrap.position.set(x, GROUND_Y, z)
    wrap.rotation.y = facing(rotation)
    root.add(wrap)
    wrap.traverse((o) => { if (o.userData?.slab) { own.push(o.geometry); if (o.material) own.push(o.material) } })
    placed.push({ wrap, k, recipeId })
    return wrap
  }
  for (const { q, r } of coords) {
    const k = key(q, r)
    const { x, z } = axialToWorld(q, r)
    const plot = plots[k], b = buildingAt.get(k)
    const t = { key: k, x, y: GROUND_Y, z }
    if (b && BuildingRecipe[b.type] && place(BuildingRecipe[b.type], k, { rotation: b.rotation })) continue
    if (plot?.type === PlotTypes.HOME) { homeKey = k; place(HomeRecipe[plot.homeLevel === 'MIDDLE' ? 'MIDDLE' : 'SHACK'], k) ?? open.push(t); continue }
    if (plot?.type === PlotTypes.FARM) { if (!place(FarmRecipe, k, { slabColor: SeasonConfig[season]?.paddyColor ?? SeasonConfig.HARVEST?.paddyColor })) open.push(t); continue }
    if (!plot || plot.type === PlotTypes.WILD) forest.push(t); else open.push(t)
  }
  progress(0.5)
  for (const [list, id] of [[open, GroundRecipe], [forest, WeedsRecipe]]) {
    const g = list.length && hasRecipe(id) ? buildGroundCover(list, id) : null
    if (g) root.add(g)
  }
  root.add(buildSyntySurroundings())
  progress(0.75)
  for (const { wrap, recipeId } of placed) if (recipeId !== FarmRecipe) for (const c of sampleBlockedCells(wrap)) blocked.add(c) // the paddy is walkable
  const isOpen = (x, z) => isInFieldWorld(x, z, FIELD_RADIUS) && !isBlockedIn(blocked, x, z)

  // ── the people ──
  const home = homeKey ? (([q, r]) => axialToWorld(q, r))(homeKey.split(',').map(Number)) : { x: 0, z: 0 }
  const pickPoint = (cx, cz, rMin, rMax, tries = 24) => {
    for (let i = 0; i < tries; i++) {
      const a = Math.random() * Math.PI * 2, d = rMin + Math.random() * (rMax - rMin)
      const x = cx + Math.cos(a) * d, z = cz + Math.sin(a) * d
      if (isOpen(x, z)) return { x, z }
    }
    return null
  }
  const stroll = (obj, cx, cz, rMax) => {
    if (disposed || !obj.parent) return
    const to = pickPoint(cx, cz, 0.4, rMax) ?? { x: obj.position.x, z: obj.position.z }
    const dx = to.x - obj.position.x, dz = to.z - obj.position.z, dist = Math.hypot(dx, dz)
    if (dist > 0.05) obj.rotation.y = Math.atan2(dx, dz)
    gsap.to(obj.position, { x: to.x, z: to.z, duration: Math.max(1.5, dist * 1.6), ease: 'none', onComplete: () => gsap.delayedCall(1.5 + Math.random() * 4, () => stroll(obj, cx, cz, rMax)) })
  }
  const people = []
  const addPerson = (kind, n, cx, cz, rMax) => {
    for (let i = 0; i < n; i++) {
      const obj = syntyCharsReady() ? createSyntyChar(kind) : null
      if (!obj) return
      const p = pickPoint(cx, cz, 0.6, rMax) ?? { x: cx, z: cz }
      obj.position.set(p.x, GROUND_Y, p.z)
      obj.scale.setScalar(scaleOf(obj))
      root.add(obj); people.push(obj)
      gsap.delayedCall(Math.random() * 2, () => stroll(obj, cx, cz, rMax))
    }
  }
  const c = snap.citizens ?? {}
  addPerson('villager_f', Math.min(c.PEASANT_CAT ?? 0, KingdomView.maxPeasants), home.x, home.z, HEX_SIZE * 3.6)
  addPerson('villager_m', Math.min(c.SCRIBE_CAT ?? 0, KingdomView.maxScribes), home.x, home.z, HEX_SIZE * 3.6)
  addPerson('geisha', Math.min((snap.wives ?? []).length, KingdomView.maxWives), home.x, home.z, HEX_SIZE * 1.5) // his wives stroll round the homestead
  progress(0.9)

  // the host himself, by his door, with his name over his head
  const hostCat = createCatFigure(snap.host?.catColorId)
  const hp = pickPoint(home.x, home.z, HEX_SIZE * 0.95, HEX_SIZE * 1.5) ?? { x: home.x + 1.2, z: home.z + 1.2 }
  hostCat.position.set(hp.x, GROUND_Y, hp.z)
  root.add(hostCat)
  const sign = nameSprite(snap.host?.name ?? 'Your host', `${snap.host?.title ? String(snap.host.title).replace(/_/g, ' ') : 'Mao'} · ${snap.host?.kingdom ?? ''}`.trim())
  sign.position.set(hp.x, GROUND_Y + 2.1, hp.z)
  root.add(sign)
  own.push(...sign.userData.disposable)

  // where the guest arrives: a few tiles out from the home, on the camera's side, on open ground
  const sp = pickPoint(home.x + HEX_SIZE * 2, home.z + HEX_SIZE * 2, 0, HEX_SIZE * 1.6, 60) ?? pickPoint(home.x, home.z, HEX_SIZE * 1.5, HEX_SIZE * 4, 60) ?? { x: home.x + 2, z: home.z + 2 }
  progress(1)

  return {
    root,
    kind: 'host-kingdom',
    colliders: blocked.size,
    spawn: { x: sp.x, y: GROUND_Y, z: sp.z },
    walk: (wx, wz) => (isOpen(wx, wz) ? GROUND_Y : null),
    // per frame: the host's idle animation, and he turns to look at the guest when they come near
    tick(dt, hx, hz) {
      hostCat.userData.tick?.(dt)
      const dx = hx - hostCat.position.x, dz = hz - hostCat.position.z
      if (Math.hypot(dx, dz) < HEX_SIZE * 2.2) { const want = Math.atan2(dx, dz); let d = want - hostCat.rotation.y; d = Math.atan2(Math.sin(d), Math.cos(d)); hostCat.rotation.y += d * Math.min(1, dt * 4) }
    },
    // the picture of what is here (for tests and the guest card)
    info: () => ({ tiles: coords.length, placed: placed.map((p) => `${p.recipeId}@${p.k}`), people: people.length, wives: Math.min((snap.wives ?? []).length, KingdomView.maxWives), host: snap.host?.name ?? null }),
    dispose() {
      disposed = true
      for (const o of [...people, hostCat]) { gsap.killTweensOf(o.position); gsap.killTweensOf(o.rotation); o.parent?.remove(o) }
      for (const o of people) o.userData.mat?.dispose?.()
      root.traverse((o) => { if (o.isInstancedMesh) o.dispose?.() })
      for (const d of own) d?.dispose?.()
      root.parent?.remove(root)
    },
  }
}
